import React from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Platform } from 'react-native';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius, PaprikaShadows } from '../../styles/theme';

interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.message}>{message}</Text>

          <View style={styles.buttons}>
            <TouchableOpacity style={styles.cancelButton} onPress={onCancel} testID="confirmation-cancel">
              <Text style={styles.cancelButtonText}>{cancelLabel}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.confirmButton,
                destructive ? styles.confirmButtonDestructive : styles.confirmButtonAccent,
              ]}
              onPress={onConfirm}
              testID="confirmation-confirm"
            >
              <Text style={styles.confirmButtonText}>{confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    marginHorizontal: PaprikaSpacing.lg,
    minWidth: 280,
    maxWidth: 340,
    ...PaprikaShadows.modal,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  message: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: PaprikaSpacing.lg,
  },
  buttons: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: Paprika.surfaceLight,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  cancelButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
  },
  confirmButtonDestructive: {
    backgroundColor: Paprika.destructive,
  },
  confirmButtonAccent: {
    backgroundColor: Paprika.accent,
  },
  confirmButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },
});

export default ConfirmationModal;
